import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { usePublisherData, usePublisherAppDetails } from '../../../hooks/useReactQueryHooks';
import { api} from '../../../utils/variables';

// Create context
const OptimizedPublisherAnalysisContext = createContext();

// Custom hook to use the context
export const useOptimizedPublisherAnalysis = () => {
  const context = useContext(OptimizedPublisherAnalysisContext);
  if (!context) {
    throw new Error('useOptimizedPublisherAnalysis must be used within an OptimizedPublisherAnalysisProvider');
  }
  return context;
};

// Provider component
export const OptimizedPublisherAnalysisProvider = ({ children, gameID }) => {
  // State for tabs and filtering
  const [activeTab, setActiveTab] = useState('meta');
  const [selectedPlatform, setSelectedPlatform] = useState('meta');
  // Export modal visibility state (shared across components)
  const [showExportModal, setShowExportModal] = useState(false);

  // React Query hooks for data fetching
  const {
    data: publisherData,
    isLoading: loader,
    error,
    refetch: refetchPublisherData
  } = usePublisherData(gameID, !!gameID);

  // Extract data from publisher response
  const games = publisherData?.data || [];
  const additionalData = publisherData || {};
  const dataAction = publisherData?.action || 'loading';

  // Find most downloaded and grossing apps
  const { mostDownloadedApp, mostGrossingApp } = useMemo(() => {
    if (!games || games.length === 0) {
      return { mostDownloadedApp: null, mostGrossingApp: null };
    }

    const mostDownloaded = games.reduce((max, game) =>
      (game.downloads || 0) > (max.downloads || 0) ? game : max
    );

    const mostGrossing = games.reduce((max, game) =>
      (game.revenue || 0) > (max.revenue || 0) ? game : max
    );

    return { mostDownloadedApp: mostDownloaded, mostGrossingApp: mostGrossing };
  }, [games]);

  // Get detailed app information for publisher name
  const {
    data: appDetails,
    isLoading: appDetailsLoading
  } = usePublisherAppDetails(mostDownloadedApp?.item_id, !!mostDownloadedApp?.item_id);

  // Format number utility function
  const formatNumber = (num) => {
    if (num >= 1e6) {
      return (num / 1e6).toFixed(1) + "M";
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + "K";
    }
    return num;
  };

  // Computed publisher info
  const publisher = useMemo(() => {
    let publisherName = null;

    if (appDetails?.publisher) {
      publisherName = appDetails.publisher;
    } else if (games.length > 0) {
      publisherName = `Publisher of ${games.length} app${games.length !== 1 ? 's' : ''}`;
    }

    return {
      name: publisherName || '',
      logo: null
    };
  }, [appDetails, games.length]);

  // Computed game cards
  const gameCards = useMemo(() => ({
    mostDownloaded: {
      name: mostDownloadedApp?.name || 'N/A',
      image: mostDownloadedApp?.image_url || null,
      id: mostDownloadedApp?.item_id || null
    },
    highestGrossing: {
      name: mostGrossingApp?.name || 'N/A',
      image: mostGrossingApp?.image_url || null,
      id: mostGrossingApp?.item_id || null
    },
    topCategories: additionalData?.top_categories || []
  }), [mostDownloadedApp, mostGrossingApp, additionalData]);

  // Computed stats
  const stats = useMemo(() => ({
    downloads: {
      value: formatNumber(additionalData?.total_downloads || 0),
      change: '+0%'
    },
    revenue: {
      value: `$${formatNumber(additionalData?.total_revenue || 0)}`,
      change: '+0%'
    },
    games: {
      value: formatNumber(additionalData?.total_apps || 0)
    }
  }), [additionalData, formatNumber]);

  // Publisher tracking data (from external tracking API)
  const [trackingData, setTrackingData] = useState(null);
  const [trackingError, setTrackingError] = useState(null);

  // Helper to fetch publisher tracking info from backend.triviumpro.in
  const fetchPublisherTracking = async (itemIdParam) => {
    const itemId = itemIdParam || gameID || mostDownloadedApp?.item_id || mostDownloadedApp?.id;
    if (!itemId) return null;

    try {
      const token = localStorage.getItem('authToken');
      const url = `${api}/tracker/tracking/publisher?item_id=${encodeURIComponent(itemId)}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Tracking API error: ${resp.status} ${text}`);
      }

      const data = await resp.json();
      setTrackingData(data);
      setTrackingError(null);
      return data;
    } catch (err) {
      console.error('Error fetching publisher tracking:', err);
      setTrackingError(err);
      setTrackingData(null);
      return null;
    }
  };

  // Auto-fetch tracking when provider mounts or when gameID / mostDownloadedApp changes
  useEffect(() => {
    // fire-and-forget; consumer can call fetchPublisherTracking manually as well
    if (gameID || mostDownloadedApp?.item_id) {
      fetchPublisherTracking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameID, mostDownloadedApp?.item_id]);

  // Filtered apps based on platform/tab
  const filteredApps = useMemo(() => {
    if (!games || games.length === 0) return [];

    if (activeTab === 'all') return games;

    // Normalize platform names for comparison
    const normalizedPlatform = activeTab.toLowerCase();
    return games.filter(game => {
      const gamePlatform = game?.platform?.toLowerCase();
      return gamePlatform === normalizedPlatform;
    });
  }, [games, activeTab]);

  // Handle platform change with optimized filtering
  const handlePlatformChange = (platform) => {
    setSelectedPlatform(platform);
    setActiveTab(platform);
    // No need to refetch data, filtering is handled by useMemo
  };

  // Cache data in session storage when it changes
  useEffect(() => {
    if (games && games.length > 0) {
      sessionStorage.setItem("categoryAppsPub", JSON.stringify(games));
      sessionStorage.setItem("filteredAppsPub", JSON.stringify(filteredApps));
      sessionStorage.setItem("categoryData", JSON.stringify(games));
    }
  }, [games, filteredApps]);

  // Value object to be provided to consumers
  const value = {
    // State
    publisher,
    activeTab,
    setActiveTab,
    gameCards,
    stats,
    selectedPlatform,
    loader: loader || appDetailsLoading,
    dataAction,
    error,

    // Data
    categoryApps: games,
    filteredApps,
    tableData: filteredApps,

    // Functions
    handlePlatformChange,
    fetchPublisherData: refetchPublisherData,
    // Publisher tracking (external API)
    trackingData,
    trackingError,
    fetchPublisherTracking,
    formatNumber,
    // Export modal controls (used by tabs/header and data table)
    showExportModal,
    setShowExportModal,

    // Computed values
    hasData: games.length > 0,
    isEmpty: !loader && games.length === 0,
    isError: !!error,
  };

  return (
    <OptimizedPublisherAnalysisContext.Provider value={value}>
      {children}
    </OptimizedPublisherAnalysisContext.Provider>
  );
};
